import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, CheckCircle2, AlertTriangle, Clock, XCircle, Sparkles, Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { http, apiError, money } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { toast } from "sonner";

const PLAN_FEATURES = ["Unlimited patient memos", "Quick search & recall", "Chairside note capture", "Support included"];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

function statusCopy(sub) {
  if (!sub) return { text: "Loading…", badge: "border-slate-300 bg-slate-100 text-slate-600", icon: Clock };
  switch (sub.status) {
    case "trialing":
      return {
        text: `Free trial · ${sub.days_left} day${sub.days_left === 1 ? "" : "s"} left`,
        badge: sub.days_left <= 7 ? "border-amber-200 bg-amber-50 text-amber-700" : "dm-badge-accent",
        icon: Clock,
      };
    case "active":
      return {
        text: `Active · renews ${fmtDate(sub.current_period_end)}${sub.billing_cycle ? ` · ${sub.billing_cycle}` : ""}`,
        badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
        icon: CheckCircle2,
      };
    case "past_due":
      return { text: "Payment failed — we're retrying", badge: "border-red-200 bg-red-50 text-red-700", icon: AlertTriangle };
    case "cancelled":
      return {
        text: `Cancelled · access until ${fmtDate(sub.current_period_end)}`,
        badge: "border-slate-300 bg-slate-100 text-slate-600",
        icon: XCircle,
      };
    case "expired":
      return {
        text: sub.billing_cycle ? "Your subscription has ended" : "Your trial has ended",
        badge: sub.billing_cycle ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700",
        icon: XCircle,
      };
    default:
      return { text: sub.status || "Unknown", badge: "border-slate-300 bg-slate-100 text-slate-600", icon: Clock };
  }
}

export default function Billing() {
  const { subscription, refreshSubscription, isOwner, accent } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null); // GET /billing full payload (owner only really needs this)
  const [checkoutBusy, setCheckoutBusy] = useState(null); // "monthly" | "annual" | "cancel" | null

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await http.get("/billing");
      setBilling(res);
      await refreshSubscription();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sub = billing || subscription;
  const copy = statusCopy(sub);
  const StatusIcon = copy.icon;

  const subscribe = async (cycle) => {
    setCheckoutBusy(cycle);
    try {
      const { data: res } = await http.post("/billing/subscribe", { cycle });
      await openRazorpayCheckout({
        key: res.key_id,
        subscription_id: res.subscription_id,
        name: res.name,
        description: res.description,
        prefill: res.prefill,
        themeColor: accent,
        onSuccess: async (resp) => {
          try {
            await http.post("/billing/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            const fresh = await refreshSubscription();
            toast.success("Subscription activated — welcome back!");
            if (fresh.has_access) navigate("/app");
          } catch (e) {
            toast.error(apiError(e));
          } finally {
            setCheckoutBusy(null);
          }
        },
        onDismiss: () => setCheckoutBusy(null),
      });
    } catch (e) {
      toast.error(apiError(e));
      setCheckoutBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setCheckoutBusy("cancel");
    try {
      await http.post("/billing/cancel");
      await refreshSubscription();
      toast.success("Subscription will end at the current period's close");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCheckoutBusy(null);
    }
  };

  const disabled = loading || billing?.billing_enabled === false;

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div>
        <h1 className="dm-page-title">
          Subscription <span className="font-extrabold">&amp; billing</span>
        </h1>
      </div>

      <section className="dm-panel p-5 md:p-7" data-testid="billing-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <StatusIcon size={18} className="text-slate-500" />
            <h2 className="font-head text-lg font-semibold text-slate-800">Status</h2>
          </div>
          <span data-testid="billing-status-badge" className={`dm-badge ${copy.badge}`}>{sub?.status || "…"}</span>
        </div>
        <p className="mt-3 text-sm text-slate-600" data-testid="billing-status-text">{copy.text}</p>
      </section>

      {isOwner ? (
        <>
          {billing?.billing_enabled === false && (
            <div className="dm-panel border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800" data-testid="billing-not-configured">
              Payments aren't configured yet. Please check back soon.
            </div>
          )}

          {sub?.status === "active" ? (
            <section className="dm-panel p-5 md:p-7">
              <h2 className="font-head text-lg font-semibold text-slate-800">Manage subscription</h2>
              <p className="mt-2 text-sm text-slate-600">
                You're on the {sub.billing_cycle || "current"} plan, renewing {fmtDate(sub.current_period_end)}.
              </p>
              <button
                data-testid="cancel-subscription-btn"
                onClick={cancel}
                disabled={checkoutBusy === "cancel"}
                className="dm-btn-danger mt-5 disabled:opacity-50"
              >
                <XCircle size={15} /> {checkoutBusy === "cancel" ? "Cancelling…" : "Cancel subscription"}
              </button>
            </section>
          ) : (
            <section>
              <h2 className="font-head text-lg font-semibold text-slate-800">Choose a plan</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                {(() => {
                  const monthlyAmount = billing?.monthly_amount || 29900;
                  const annualAmount = billing?.annual_amount || 299900;
                  const savings = monthlyAmount * 12 - annualAmount;
                  const perMonth = Math.round(annualAmount / 12);
                  const plans = [
                    {
                      key: "monthly", name: "Monthly", price: money(monthlyAmount / 100), unit: "/month",
                      lines: ["Billed every month", "Cancel anytime, no lock-in"], emphasize: false,
                    },
                    {
                      key: "annual", name: "Annual", price: money(annualAmount / 100), unit: "/year", badge: "Best Value",
                      lines: [
                        `${money(perMonth / 100)}/month, billed annually`,
                        { strike: money((monthlyAmount * 12) / 100), save: `Save ${money(savings / 100)}/year` },
                      ],
                      emphasize: true,
                    },
                  ];
                  return plans.map((p) => (
                    <div
                      key={p.key}
                      className={`dm-panel flex flex-col p-6 ${p.emphasize ? "border-2 border-[var(--dm-accent)] shadow-[0_20px_50px_-24px_rgba(37,99,235,0.35)]" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-head text-base font-bold text-slate-900">{p.name}</h3>
                        {p.badge && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-teal-700">
                            <Sparkles size={11} /> {p.badge}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="font-head text-2xl font-extrabold tracking-tight text-slate-900">{p.price}</span>
                        <span className="text-sm text-slate-500">{p.unit}</span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-slate-500">
                        {p.lines.map((line, i) =>
                          typeof line === "string" ? (
                            <p key={i}>{line}</p>
                          ) : (
                            <p key={i}>
                              <s className="text-slate-400">{line.strike}</s>{" "}
                              <span className="font-semibold text-teal-700">{line.save}</span>
                            </p>
                          )
                        )}
                      </div>
                      <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
                        {PLAN_FEATURES.map((f) => (
                          <li key={f} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-teal-600" />{f}</li>
                        ))}
                      </ul>
                      <button
                        data-testid={`subscribe-${p.key}-btn`}
                        onClick={() => subscribe(p.key)}
                        disabled={disabled || checkoutBusy}
                        className={`mt-5 w-full justify-center disabled:opacity-50 ${p.emphasize ? "dm-btn-primary" : "dm-btn-ghost"}`}
                      >
                        <CreditCard size={15} /> {checkoutBusy === p.key ? "Opening checkout…" : "Get DentMemo"}
                      </button>
                    </div>
                  ));
                })()}
              </div>
            </section>
          )}
        </>
      ) : (
        <section className="dm-panel p-5 md:p-7" data-testid="billing-assistant-notice">
          <p className="text-sm text-slate-600">
            Ask your clinic owner to subscribe from Settings → Billing to restore access.
          </p>
        </section>
      )}
    </div>
  );
}
